import { useCallback, useEffect, useState } from "react";

export type RouteKey =
  | "home"
  | "collection"
  | "portfolio"
  | "trade"
  | "rewards"
  | "loans"
  | "protocol";

export const routePaths: Record<RouteKey, string> = {
  home: "/",
  collection: "/collection",
  portfolio: "/app/portfolio",
  trade: "/app/trade",
  rewards: "/app/rewards",
  loans: "/app/loans",
  protocol: "/protocol",
};

export const applicationRoutes: RouteKey[] = [
  "portfolio",
  "trade",
  "rewards",
  "loans",
];

const routeByPath = new Map(
  Object.entries(routePaths).map(([key, path]) => [path, key as RouteKey]),
);

export function routeForPath(pathname: string): RouteKey {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return routeByPath.get(normalized) || "home";
}

export function useRoute() {
  const [route, setRoute] = useState<RouteKey>(() =>
    routeForPath(window.location.pathname),
  );

  useEffect(() => {
    const handlePopState = () =>
      setRoute(routeForPath(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const titles: Record<RouteKey, string> = {
      home: "Stonkland — Broker NFTs and RWA emissions",
      collection: "Broker collection, art and traits | Stonkland",
      portfolio: "Broker portfolio | Stonkland",
      trade: "Trade LAND and Brokers | Stonkland",
      rewards: "Broker rewards | Stonkland",
      loans: "Borrow against a Broker | Stonkland",
      protocol: "How Stonkland works",
    };
    document.title = titles[route];
  }, [route]);

  const navigate = useCallback((next: RouteKey) => {
    const path = routePaths[next];
    if (window.location.pathname !== path)
      window.history.pushState({}, "", path);
    setRoute(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return { route, navigate };
}
